import {
  useGetAnalysisList,
  AnalysisListInfo,
  analysisAreaTypes,
  analysisCategories
} from '@domain/rcfa'
import {StoppageCodeLabel} from '@domain/stoppages'
import {DataTable} from '@hconnect/common/maintain-datatable'
import {OrderByApiProperty, useListQuerySync, ApiFilter} from '@hconnect/common/maintain-filters'
import {AnalysisListType} from '@hconnect/common/types'
import {Loader} from '@hconnect/uikit/src/lib2'
import {Box, useTheme} from '@mui/material'
import React, {useEffect} from 'react'

import {FEATURE_NAMESPACE} from '../../localization'
import {formatDate, formatDateTime} from '../../utils'
import {usePlantUpmId} from '../common'
import {DeletedStoppageWarning} from '../common/DeletedStoppageWarning'

import {AnalysisListColumn, AnalysisListProperty} from './analysisList.types'
import {TableCellNavigate} from './AnalysisListItem'
import {AnalysisListNoData} from './AnalysisListNoData'
import {AnalysisStatusComponent} from './AnalysisStatus'
import {ItemActions} from './ItemActions'

export const AnalysisListTable: React.FC<{
  page: number
  listType: AnalysisListType
  rowsNumber: number
  setCanCreateAnalysis: React.Dispatch<React.SetStateAction<boolean>>
  handleOrderToggle: (col: AnalysisListProperty) => void
  availableColumns: AnalysisListColumn[]
  filters?: ApiFilter[]
  order?: OrderByApiProperty[]
}> = ({
  page,
  rowsNumber,
  setCanCreateAnalysis,
  listType,
  filters,
  order,
  availableColumns,
  handleOrderToggle
}) => {
  const {palette} = useTheme()
  const plantUpmId = usePlantUpmId()
  const {data, isFetching} = useGetAnalysisList({
    plantUpmId,
    page,
    rowsNumber,
    listType,
    filters,
    order
  })
  const {handlePageChange, handleRowsPerPageChange} = useListQuerySync({
    filters,
    order,
    filtersEnabled: true,
    orderEnabled: true
  })

  useEffect(() => {
    setCanCreateAnalysis(data?.permissions ? data.permissions.canCreateAnalysis : false)
  }, [data, setCanCreateAnalysis])

  // eslint-disable-next-line complexity
  const customColumnRender = (columnName: string, data: AnalysisListInfo) => {
    const analysisRoute = `../graphDetail/${data.id}/graph`
    switch (columnName) {
      case 'StoppageCode':
        return (
          <TableCellNavigate to={analysisRoute} data-test-id={`analysis-${data.id}-stoppage-code`}>
            {data.stoppageIsDeleted ? (
              <DeletedStoppageWarning />
            ) : (
              <StoppageCodeLabel
                stoppageCode={
                  data.stoppageCode && data.stoppageCodeText
                    ? {code: data.stoppageCode, text: data.stoppageCodeText}
                    : undefined
                }
                groupStoppages={data?.groupStoppages}
                titleVariant="body2"
                emptyPlaceholder=""
              />
            )}
          </TableCellNavigate>
        )
      case 'CreateDate':
        return (
          <TableCellNavigate to={analysisRoute}>{formatDate(data.createDate)}</TableCellNavigate>
        )
      case 'Title':
        return (
          <TableCellNavigate to={analysisRoute}>
            <Box sx={{fontWeight: 500, fontSize: '15px'}}>{data.title}</Box>
          </TableCellNavigate>
        )
      case 'LastUpdate':
        return (
          <TableCellNavigate to={analysisRoute}>{formatDate(data.updateDate)}</TableCellNavigate>
        )
      case 'DueDate':
        return <TableCellNavigate to={analysisRoute}>{formatDate(data.dueDate)}</TableCellNavigate>
      case 'Contributors':
        return <TableCellNavigate to={analysisRoute}>{data.contributors}</TableCellNavigate>
      case 'PlantName':
        return (
          <TableCellNavigate
            to={analysisRoute}
          >{`${data.plant} (${data.country})`}</TableCellNavigate>
        )
      case 'Owner':
        return <TableCellNavigate to={analysisRoute}>{data.owner?.name}</TableCellNavigate>
      case 'Status':
        return (
          <TableCellNavigate to={analysisRoute}>
            {data.status ? (
              <AnalysisStatusComponent
                status={data.status}
                hasPendingTasks={data.hasPendingTasks}
              />
            ) : (
              ''
            )}
          </TableCellNavigate>
        )
      case 'stoppageStart':
        return (
          <TableCellNavigate to={analysisRoute}>
            {formatDateTime(data.stoppageStart)}
          </TableCellNavigate>
        )
      case 'stoppageEnd':
        return (
          <TableCellNavigate to={analysisRoute}>
            {formatDateTime(data.stoppageEnd)}
          </TableCellNavigate>
        )
      case 'category':
        return (
          <TableCellNavigate to={analysisRoute}>
            {data.category ? analysisCategories.find((c) => c.id === data.category)?.label : ''}
          </TableCellNavigate>
        )
      case 'areaTypeCode':
        return (
          <TableCellNavigate to={analysisRoute}>
            {data.areaTypeCode
              ? analysisAreaTypes.find((type) => type.id === data.areaTypeCode)?.label
              : ''}
          </TableCellNavigate>
        )
      case 'actions':
        return <ItemActions analysis={data} />
      default:
        return <TableCellNavigate to={analysisRoute}>{data[columnName] ?? ''}</TableCellNavigate>
    }
  }

  return (
    <Box>
      {isFetching && <Loader />}
      {!isFetching && data && data.data.length > 0 && (
        <DataTable<AnalysisListProperty, AnalysisListInfo>
          tableId={`analysis-list-${listType}`}
          paginationSettings={{
            page,
            rowsNumber,
            handlePageChange,
            handleRowsPerPageChange
          }}
          tableHeaderSettings={{
            hideHeader: true
          }}
          orderSettings={{
            handleOrderToggle,
            order
          }}
          availableColumns={availableColumns}
          hoverColor={palette.grey.A100}
          isLoadingData={isFetching}
          data={data}
          hideWrapper
          customColumnRender={customColumnRender}
          showColumnSelection
          translationNamespace={FEATURE_NAMESPACE}
        />
      )}
      {!isFetching && !data?.data?.length && (
        <AnalysisListNoData
          listType={listType}
          filtersActive={Array.isArray(filters) && filters.length > 0}
        />
      )}
    </Box>
  )
}
